import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import InputField from '@/components/atoms/InputField';
import euroIcon from '@/assets/icons/euro.svg';

// Import the form schema from types
import { createExpenseCategoryFormSchema, ExpenseCategoryFormValues } from '@/services/types/expense-category.types';
import TextAreaField from '@/components/atoms/TextAreaField';
import SliderInputField from '@/components/molecules/SliderInputField';
import SliderField from '@/components/atoms/SliderField';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface ECCreationFormProps {
  onSubmit?: (data: ExpenseCategoryFormValues) => void | Promise<void>;
  className?: string;
  isSubmitting?: boolean;
  defaultValues?: Partial<ExpenseCategoryFormValues>;
  isEditMode?: boolean;
  maxExpenseValue?: number;
  onFormStateChange?: (state: { isDirty: boolean }) => void;
  onCancel?: () => void;
}

const ECCreationForm: React.FC<ECCreationFormProps> = ({
  onSubmit,
  className,
  isSubmitting = false,
  defaultValues,
  isEditMode = false,
  maxExpenseValue,
  onFormStateChange,
  onCancel
}) => {
  const navigate = useNavigate();
  const [ecmevError, setEcmevError] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  const formSchema = useMemo(() => createExpenseCategoryFormSchema(maxExpenseValue), [maxExpenseValue]);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isDirty }
  } = useForm<ExpenseCategoryFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues || {
      name: '',
      description: '',
      ecmev: 0,
      ecmev_alert_threshold: 0
    }
  });

  // Reset form when defaultValues change
  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  useEffect(() => {
    onFormStateChange?.({ isDirty });
  }, [isDirty, onFormStateChange]);

  const ecmev = watch('ecmev');
  const ecmev_alert_threshold = watch('ecmev_alert_threshold');
  const isBusy = isSubmitting || isFormSubmitting;

  // Debug: log errors if any
  if (Object.keys(errors).length > 0) {
    console.log('Form errors:', errors);
  }

  const onSubmitForm = async (data: ExpenseCategoryFormValues) => {
    if (onSubmit) {
      setIsFormSubmitting(true);
      try {
        await Promise.resolve(
          onSubmit({
            ...data,
            ecmev: Number(data.ecmev),
            ecmev_alert_threshold: Number(data.ecmev_alert_threshold)
          })
        );
      } finally {
        setIsFormSubmitting(false);
      }
    }
  };

  const onInvalidForm = () => {
    toast.error('Please correct the invalid form fields');
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    navigate(-1);
  };

  const handleEcmevExceed = () => {
    toast.error(`ECMEV cannot exceed BMEV (€${maxExpenseValue?.toLocaleString()})`);
    setEcmevError(true);
    setTimeout(() => setEcmevError(false), 3000);
  };

  return (
    <>
      <form className={`flex-1 flex flex-col gap-4 ${className}`} onSubmit={handleSubmit(onSubmitForm, onInvalidForm)}>
        <div className="space-y-5 flex-1">
          <div className="space-y-6 w-full flex flex-col items-start justify-between md:flex-row md:gap-5 md:space-y-0">
            <InputField
              className="flex-1 w-full"
              label="Expense category name"
              placeholder="Enter expense category name..."
              name="name"
              type="text"
              register={register}
              error={errors.name}
              disabled={isBusy}
              size="sm"
            />
            <span className="hidden md:block flex-1"></span>
          </div>

          <div className="space-y-6 w-full flex flex-col items-start justify-between md:flex-row md:gap-5 md:space-y-0">
            <TextAreaField
              name="description"
              label="Expense category Description"
              placeholder="Enter expense category description..."
              register={register}
              className="w-full"
              error={errors.description}
              labelClassName="md:text-sm"
              disabled={isBusy}
            />
          </div>

          <div className="space-y-6 w-full flex flex-col items-start justify-between md:flex-row md:gap-5 md:space-y-0">
            <div className="flex-1 w-full">
              <SliderInputField
                name="ecmev"
                label="Expense category max expense value (ECMEV)"
                inputPlaceholder="e.g 100000"
                value={Number(ecmev) || 0}
                onChange={(val) => setValue('ecmev', val as number, { shouldDirty: true })}
                error={errors.ecmev || ecmevError}
                min={0}
                maxValue={maxExpenseValue || 100000}
                icon={euroIcon}
                tooltipText={`ECMEV cannot exceed BMEV (€${maxExpenseValue?.toLocaleString()})`}
                onValueExceed={handleEcmevExceed}
              />

              <p className={`text-sm mt-[0.35rem] ${ecmevError ? 'text-red-500' : 'text-secondary'}`}>
                ECMEV cannot exceed BMEV (€{maxExpenseValue?.toLocaleString()})
              </p>
            </div>
            <SliderField
              name="ecmev_alert_threshold"
              label="Alerting threshold of ECMEV"
              value={[Number(ecmev_alert_threshold) || 0]}
              onValueChange={([value]) => setValue('ecmev_alert_threshold', value, { shouldDirty: true })}
              className="flex-1 w-full"
              error={errors.ecmev_alert_threshold}
            />
          </div>

          <div className="space-y-6 w-full flex flex-col items-start justify-between md:flex-row md:gap-5 md:space-y-0">
            <span className="hidden md:block flex-1"></span>
          </div>
        </div>
        {/* FOOTER */}
        <footer className="py-2 bg-gradient-to-b from-[#F0EFFD]/0 from-0% to-[#F0EFFD] to-[17.74%] md:bg-white md:bg-none md:from-transparent md:to-transparent md:flex md:justify-end">
          <Button
            className="hidden md:block rounded-[24px] text-base font-semibold px-6 h-[44px] text-[16px] w-full md:w-fit text-[#E8507E]"
            variant={'text'}
            onClick={handleCancel}
            type="button"
          >
            Cancel
          </Button>
          <Button
            className="rounded-[24px] text-base font-semibold px-6 h-[44px] text-[16px] w-full md:w-fit"
            type="submit"
            disabled={!isDirty || isBusy}
          >
            {isBusy ? (isEditMode ? 'SAVING...' : 'CREATING...') : isEditMode ? 'SAVE' : 'CREATE'}
          </Button>
        </footer>
      </form>
    </>
  );
};

export default ECCreationForm;
